import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Route } from "lucide-react";
import { RequestDetailsLoader } from "@/components/RequestDetailsPanel";

const TONE = { done: "text-emerald-700", skipped: "text-slate-400", pending: "text-slate-500", rejected: "text-rose-700", sent_back: "text-orange-700", cancelled: "text-slate-400" };
const STATUS = { done: "Done", skipped: "Skipped", pending: "Upcoming", rejected: "Rejected", sent_back: "Sent back", cancelled: "Not needed" };

const Step = ({ s, isCurrent }) => (
  <li className={`border-l-2 pl-3 pb-3 ${isCurrent ? "border-amber-400" : "border-slate-200"}`} data-testid={`chain-step-${s.key}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium text-sm">{s.label}</span>
      <span className={`text-xs font-semibold ${isCurrent ? "text-amber-700" : TONE[s.status]}`}>{isCurrent ? "Waiting" : STATUS[s.status]}</span>
    </div>
    {s.status === "done" && <div className="text-xs text-slate-600">{s.by_name}{s.by_role ? ` (${s.by_role})` : ""} · {(s.at || "").slice(0, 16).replace("T", " ")}{s.comment ? ` — ${s.comment}` : ""}</div>}
    {(s.status === "rejected" || s.status === "sent_back") && <div className="text-xs text-slate-600">{s.by_name} — {s.comment}</div>}
    {s.status === "pending" && <div className="text-xs text-slate-500">Assigned to: {(s.assignee_names || []).length > 4 ? `${s.assignee_names.slice(0, 3).join(", ")} +${s.assignee_names.length - 3} more` : (s.assignee_names?.join(", ") || "nobody yet")}</div>}
    {s.skip_reason && <div className="text-xs text-slate-500">{s.skip_reason}</div>}
    {s.corrected_from && <div className="text-xs text-slate-500" data-testid={`chain-corrected-${s.key}`}>Originally endorsed by {s.corrected_from.by_name} on {(s.corrected_from.at || "").slice(0, 16).replace("T", " ")} – counted as on-behalf endorsement</div>}
    {s.note && <div className="text-xs text-amber-700">{s.note}</div>}
  </li>
);

export const ChainButton = ({ kind, id, testId }) => {
  const [data, setData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const open = async () => {
    try { setData((await api.get(`/chain/${kind}/${id}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load route"); }
  };
  const editLink = `/app/${{ reimbursement: "reimbursements", advance: "advances", expense: "expenses-v2" }[kind]}?edit=${id}`;
  return (
    <>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={open} data-testid={testId || `chain-btn-${id}`}>
        <Route className="w-3 h-3 mr-1" /> Route
      </Button>
      <Dialog open={!!data} onOpenChange={(o) => !o && setData(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain" data-testid="chain-dialog">
          <DialogHeader><DialogTitle>Approval route · {data?.reference}</DialogTitle></DialogHeader>
          <Button size="sm" variant="outline" className="w-fit" onClick={() => setShowDetails(v => !v)} data-testid="chain-details-toggle">{showDetails ? "Hide" : "Show"} request details</Button>
          {showDetails && <RequestDetailsLoader kind={kind} id={id} />}
          {data?.combined_flag && <div className="text-xs text-rose-700" data-testid="chain-combined-flag">Combined claims this month ≥ limit — Super Admin final approval required</div>}
          <ol className="mt-2" data-testid="chain-steps">
            {(data?.chain || []).map(s => <Step key={s.key} s={s} isCurrent={data.current === s.key} />)}
          </ol>
          {!data?.chain?.length && <p className="text-sm text-slate-500">No approval route recorded (older request).</p>}
          {data?.is_requester && ["SentBack", "sent_back"].includes(data?.status) && (
            <a href={editLink} data-testid="chain-edit-link"><Button>Open Edit & resubmit</Button></a>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
